import React, { useEffect, useMemo, useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './EditorOrdineRiepilogo.module.sass'
import { OrdineRiepilogoCard } from '@components/molecules'
import { api } from '@utils/index'
import { useRouter } from 'next/router'
import {
  ADDTOMAIN,
  CARRELLO,
  MOVETOLATER,
  ORDINASUBITO,
  PERSONALIZZAZIONICARRELLO,
} from '@utils/endpoint'
import { EditorColorType, EditorErrorType, EditorStatusType } from '@utils/types'
import { useEditorContext } from 'context/EditorContext'
import { dataURLtoBlob } from '@utils/download'
import useTrans from '@hooks/useTrans'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { getError as getCartError } from '../../../utils/cart'
import { createToken } from '../../../pages/cart/checkout/[token]'
import { useSelector } from 'react-redux'
import { EDITOR_FROM_PDP } from '@utils/editor-bozze'

interface Props {
  className?: string
  personalizzazioneId: number | string | null
  carrelloId?: number | string | null
  editorState?: EditorStatusType
  errors?: EditorErrorType[]
}

const EditorOrdineRiepilogo = (props: Props) => {
  const {
    className = '',
    personalizzazioneId,
    carrelloId = null,
    editorState = null,
    errors = [],
  } = props

  const t = useTrans()
  const router = useRouter()
  const { utente } = useSelector((state) => state.utente)
  const { canvases, getActions } = useEditorContext()
  const [initialPersonalizzazioneCarrello, setInitialPersonalizzazioneCarrello] = useState()
  const [personalizzazioneCarrello, setPersonalizzazioneCarrello] = useState()

  const [isLoading, setLoading] = useState(false)
  const [initialCarrello, setInitialCarrello] = useState()
  const [carrello, setCarrello] = useState()
  const editorSource = useMemo(() => {
    const from = router.query?.from
    return Array.isArray(from) ? from[0] : from || EDITOR_FROM_PDP
  }, [router.query?.from])
  const isPdpSource = editorSource === EDITOR_FROM_PDP

  const handleInit = async () => {
    if (!personalizzazioneId) return

    try {
      const res = await api.get(`${PERSONALIZZAZIONICARRELLO}${personalizzazioneId}/`, {
        params: { lang: router.locale },
      })
      const personalizzazioneCarrelloData = res.data
      setInitialPersonalizzazioneCarrello(personalizzazioneCarrelloData)
      setPersonalizzazioneCarrello(personalizzazioneCarrelloData)

      if (!carrelloId) return

      const carrelloResponse = await api.get(`${CARRELLO}${carrelloId}/`, {
        params: { lang: router.locale },
      })
      setInitialCarrello(carrelloResponse.data)
      setCarrello(carrelloResponse.data)
    } catch (err) {
      displayNotification('error', getCartError(err, t('Qualcosa è andato storto')), 'error')
      console.error(err)
    }
  }

  useEffect(() => {
    handleInit()
  }, [personalizzazioneId, carrelloId])

  const isDirty = useMemo(() => {
    if (!personalizzazioneCarrello || !editorState) return false

    const sameTecnica =
      editorState?.tecnica?.id ===
      personalizzazioneCarrello?.posizioni?.[0]?.personalizzazione?.tecnica?.id
    if (!sameTecnica) return true

    const sameNumPosizioni =
      editorState?.posizioni?.length == personalizzazioneCarrello?.posizioni?.length
    if (!sameNumPosizioni) return true

    const samePosizioni = editorState?.posizioni?.every((p) =>
      personalizzazioneCarrello.posizioni.some(
        (pc) => pc.personalizzazione.id === p.configurazione.id
      )
    )
    if (!samePosizioni) return true

    return false
  }, [canvases, editorState, personalizzazioneCarrello])

  const dangerErrors = useMemo(() => {
    return errors?.filter((error) => error.variant === 'danger') || []
  }, [errors])

  const hasDangerErrors = dangerErrors.length > 0

  const isValid = useMemo(() => {
    const righeCarrello = carrello?.righe_carrello || []
    if (!righeCarrello?.length) return false

    if (hasDangerErrors) return false

    if (!Object.keys(canvases)?.length) return false

    return Object.keys(canvases)?.every((cId: string) => canvases[cId]?.data?.length > 0)
  }, [canvases, carrello, hasDangerErrors])

  const editorAlert = useMemo(() => {
    if (!hasDangerErrors) return undefined

    return {
      variant: 'danger' as const,
      message: t("Per procedere con l'ordine, devi prima correggere gli errori indicati"),
    }
  }, [hasDangerErrors])

  const addToCartMain = async () => {
    if (!personalizzazioneCarrello) return

    setLoading(true)
    try {
      const updatedPersonalizzazioneCarrello = await persistPersonalizzazione()
      if (!updatedPersonalizzazioneCarrello) return

      if (!isPdpSource) {
        displayNotification(
          'success.pers.edit',
          t('Hai modificato con successo la personalizzazione'),
          'success',
          () => router.push('/cart'),
          t('Vai al carrello')
        )
        router.push('/cart')
        return
      }

      if (!carrello?.id) return

      await api.post(`${CARRELLO}${carrello.id}/${ADDTOMAIN}`, {
        salvato_per_dopo: false,
        lang: router.locale,
      })
      setCarrello(null)
      displayNotification(
        'success.pers.add',
        t('Hai aggiunto la personalizzazione nel Carrello'),
        'success',
        () => router.push('/cart'),
        t('Vai al carrello')
      )
      router.push('/cart')
    } catch (err) {
      displayNotification('error', getCartError(err, t('Qualcosa è andato storto')), 'error')
    } finally {
      setLoading(false)
    }
  }

  const ordinaSubito = async () => {
    // solo pdp
    if (!carrello) return

    setLoading(true)
    try {
      await persistPersonalizzazione()

      const response = await api.post(`${CARRELLO}${carrello?.id}/${ORDINASUBITO}`, {
        lang: router.locale,
      })
      const { id } = await response.data

      // TODO: controllo che i loghi siano stati caricati
      let loghiMancanti = false
      if (loghiMancanti) {
        router.push('/cart')
      } else {
        router.push(`/cart/checkout/${createToken(id)}`)
      }
    } catch (err) {
      displayNotification(
        'error.ordinaSubito',
        getCartError(err, t('Qualcosa è andato storto')),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const saveLater = async () => {
    // solo pdp
    if (!carrello) return

    setLoading(true)
    try {
      await persistPersonalizzazione()

      await api.post(`${CARRELLO}${carrello.id}/${MOVETOLATER}`, {
        salvato_per_dopo: true,
        lang: router.locale,
      })
      setCarrello(null)
      displayNotification(
        'success.saveForLater',
        t('Hai salvato per dopo questi articoli'),
        'success',
        () => router.push('/cart'),
        t('Vai al carrello')
      )
      router.push('/cart')
    } catch (err) {
      displayNotification(
        'error.saveForLater',
        getCartError(err, t('Qualcosa è andato storto')),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const createOfferta = async () => {
    if (!personalizzazioneCarrello) return

    setLoading(true)
    try {
      const updatedPersonalizzazioneCarrello = await persistPersonalizzazione()
      const targetCartId = isPdpSource
        ? carrello?.id
        : updatedPersonalizzazioneCarrello?.carrello_id // TODO
      if (!targetCartId) return

      router.push(`/crea-offerta?cart=${targetCartId}`)
    } catch (err) {
      displayNotification('error', getCartError(err, t('Qualcosa è andato storto')), 'error')
    } finally {
      setLoading(false)
    }
  }

  const updateBozze = async (personalizzazioneCarrello) => {
    const requests =
      personalizzazioneCarrello?.posizioni?.map(async (pos) => {
        const editorPos = editorState?.posizioni?.find(
          (p) => p.configurazione?.id === pos.personalizzazione_id
        )
        const canvasId = editorPos?.id
        if (!canvasId) return null

        const actions = getActions(String(canvasId))
        if (!actions) return null

        const filenamePrefix = `${editorPos?.configurazione?.posizione?.nome || 'posizione'}`
        // La preview può includere anche gli altri colori articolo, quindi attendiamo
        // la composizione finale della PNG prima di salvarla in bozza.
        const imageDataUrl = await actions.getPreviewImage()
        if (!imageDataUrl) return null
        const imageBlob = dataURLtoBlob(imageDataUrl)
        const canvasJson = actions.saveCanvasJson()
        const canvasJsonFile = new File(
          [JSON.stringify(canvasJson)],
          `bozza_${filenamePrefix}.json`,
          { type: 'application/json' }
        )

        const formData = new FormData()
        formData.append('output_image', imageBlob, `bozza_${filenamePrefix}.png`)
        formData.append('canvas_json', canvasJsonFile)
        formData.append('colore_id', editorState?.articoloPrincipale?.colore?.id || null)
        const logoIds =
          canvases?.[canvasId]?.data
            ?.filter((oc: any) => oc.etype === 'image')
            .map((im: any) => im.id) || []
        if (logoIds.length > 0) {
          logoIds.forEach((logoId) => {
            formData.append('logo_ids', String(logoId))
          })
        }

        return api.post(
          `editor-bozze/posizione-personalizzazione/${pos.id}/update-editor-state/`,
          formData
        )
      }) || []

    if (!requests.length) return

    try {
      await Promise.all(requests as Promise<any>[])

      personalizzazioneCarrello?.posizioni?.map((pos) => {
        const editorPos = editorState?.posizioni?.find(
          (p) => p.configurazione?.id === pos.personalizzazione_id
        )
        const canvasId = editorPos?.id
        if (!canvasId) return null

        const actions = getActions(String(canvasId))
        if (!actions) return null

        // una volta salvato l'editor devo ripristinare lo stato di "non dirty" sul canvas
        actions.resetDirty(canvases?.[canvasId]?.data || [])
      })
    } catch (err) {
      displayNotification('error', getCartError(err, t('Impossibile salvare la bozza')), 'error')
      console.error(err)
      throw err
    }
  }

  const persistPersonalizzazione = async () => {
    if (!personalizzazioneCarrello) return null

    const params = {
      ...(personalizzazioneCarrello.nome_lavorazione_manuale && {
        nome_lavorazione: personalizzazioneCarrello.nome_lavorazione,
      }),
      confezionamento_richiesto: personalizzazioneCarrello.confezionamento_richiesto,
      posizioni: editorState.posizioni?.map((pos) => {
        const posizioneCarrello = personalizzazioneCarrello.posizioni.find(
          (p) => p.personalizzazione_id === pos.configurazione.id
        )
        return {
          ...posizioneCarrello,
          personalizzazione_id: pos.configurazione.id,
          personalizzazione: pos.configurazione,
        }
      }),
    }

    const response = await api.put(`${PERSONALIZZAZIONICARRELLO}${personalizzazioneId}/`, params)
    const updatedPersonalizzazioneCarrello = response.data
    setPersonalizzazioneCarrello(updatedPersonalizzazioneCarrello)

    await updateBozze(updatedPersonalizzazioneCarrello)
    await handleInit()

    return updatedPersonalizzazioneCarrello
  }

  const aggiornaPreventivo = async () => {
    if (!personalizzazioneCarrello) return

    setLoading(true)
    try {
      await persistPersonalizzazione()
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={cn(styles.root, className)}>
      <OrdineRiepilogoCard
        className={styles.content}
        ordine={isDirty ? null : carrello}
        variant="editor"
        aggiornaPreventivo={isDirty ? () => aggiornaPreventivo() : undefined}
        AddToCartMain={addToCartMain}
        // OrdinaSubito={isPdpSource ? ordinaSubito : undefined} // nascosto momentaneamente
        SaveLater={isPdpSource ? saveLater : undefined}
        onCreateOffer={createOfferta}
        disabledActions={!isValid}
        isPreventivoFormValid={isValid}
        isLoading={isLoading}
        editorAlert={editorAlert}
        editorCtas={{
          showAddToCart: !isDirty && !hasDangerErrors,
          showAggiornaPreventivo: isDirty && !hasDangerErrors,
          showOrdinaSubito: isPdpSource && !isDirty && !hasDangerErrors,
          showCreateOffer:
            isPdpSource && !isDirty && !hasDangerErrors && !!carrello?.righe_carrello?.length,
          showSaveLater: isPdpSource && !isDirty && !hasDangerErrors,
        }}
      />
    </div>
  )
}

export default EditorOrdineRiepilogo
